import React, { useContext } from "react";
import {
  Box,
  Button,
  Divider,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  Typography,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { RendererContext } from "../contexts/rendererContext";
import { T } from "./T";

type FileType = {
  name: string;
  extension: string;
  description: string | React.ReactNode;
  exportCallback: () => void;
};

const ImportExportButtons = () => {
  const rendererContext = useContext(RendererContext);
  const viewer = rendererContext.viewer;
  const [exportLashings, setExportLashings] = React.useState(false);

  const fileTypes: { [key: string]: FileType } = {
    sjor: {
      name: "sjor",
      extension: ".sjor",
      description: (
        <T str="Export the current scene as a .sjor file. This filetype can only be used in our platform to share amazing ideas with other people." />
      ),
      exportCallback: () => viewer?.saveTool.exportAll("sjorcraft_export"),
    },
    jpg: {
      name: "jpg",
      extension: ".jpg",
      description: (
        <T str="Export the current scene as a .jpg image so you can easily print it or save it offline." />
      ),
      exportCallback: () => viewer?.imageExporter.exportImage(),
    },
    stl: {
      name: "stl",
      extension: ".stl",
      description: (
        <T str="Export the current scene as an .stl file so you can import it in other 3D modeling software such as Blender, 3DS Max, Maya, etc. An .stl file is a 3D model format that is commonly used for 3D printing." />
      ),
      exportCallback: () =>
        viewer?.saveTool.exportToSTL("stl_export", exportLashings),
    },
    dae: {
      name: "dae",
      extension: ".dae",
      description: (
        <T str="Export the current scene as a .dae file so you can import it in other 3D modeling software. Such as Blender, 3DS Max, Maya, etc." />
      ),
      exportCallback: () =>
        viewer?.saveTool.exportToDAE("dae_export", exportLashings),
    },
  };

  const [type, setType] = React.useState(fileTypes.sjor);

  const onImportSjor = () => {
    viewer?.saveTool.importAll();
  };

  return (
    <Box
      sx={{
        overflowY: "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: "1rem",
          width: "100%",
        }}
      >
        <Typography variant="h5" color="primary.contrastText">
          <T str="Export to file" />
        </Typography>
        <Select
          labelId="select-export-type"
          id="select-export-type"
          value={type.name}
          label="ExportType"
          onChange={(t) => setType(fileTypes[t.target.value])}
        >
          <MenuItem value={"sjor"}>.SJOR</MenuItem>
          <MenuItem value={"jpg"}>.JPG</MenuItem>
          <MenuItem value={"dae"}>.DAE</MenuItem>
          <MenuItem value={"stl"}>.STL</MenuItem>
        </Select>
        <Typography variant="body1" color="primary.contrastText">
          {type.description}
        </Typography>
        {type.name !== "sjor" && type.name !== "jpg" && (
          <Table>
            <TableBody>
              <TableCell>
                <T str="Export Lashings" />
              </TableCell>
              <TableCell>
                <Switch
                  aria-label=""
                  color="secondary"
                  checked={exportLashings}
                  onClick={() => {
                    setExportLashings(!exportLashings);
                  }}
                />
              </TableCell>
            </TableBody>
          </Table>
        )}
        <Button
          variant="contained"
          color="secondary"
          startIcon={<FileDownloadIcon />}
          onClick={type.exportCallback}
        >
          Download {type.extension}
        </Button>
      </Box>
      <Divider
        sx={{
          margin: "1rem 0",
          backgroundColor: "secondary.light",
        }}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: "1rem",
          width: "100%",
        }}
      >
        <Typography variant="h5" color="primary.contrastText">
          <T str="Import .sjor file" />
        </Typography>
        <Typography variant="body1" color="primary.contrastText">
          <T str="Import a .sjor file that was previously exported from this platform." />
        </Typography>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<FileUploadIcon />}
          onClick={onImportSjor}
        >
          .sjor
        </Button>
        <input
          type="file"
          id="file"
          accept=".sjor"
          style={{ display: "none" }}
        />
      </Box>
    </Box>
  );
};

export default ImportExportButtons;
